import { SocketContext } from "@contexts/SocketContext";
import SupplierContext from "@contexts/SupplierContext";
import UserContext from "@contexts/UserContext";
import CustomImportTypeEnum from "@models/CustomImportTypeEnum";
import IGetCustomImportsDTO from "@models/DTO/IGetCustomImportsDTO";
import IOnGetCustomImportsDTO from "@models/DTO/IOnGetCustomImportsDTO";
import EndpointsEnum from "@models/EndpointsEnum";
import ICustomImport from "@models/ICustomImport";
import IUiSocketSetup from "@models/IUiSocketSetup";
import { ChangeEvent, useContext, useEffect, useState } from "react";
import OwSelectOption from "../OwSelectOption/OwSelectOption";
import { LoadingResults } from "../LoadingResults/LoadingResults";
import IProcessCustomImportCsvFileDTO from "@models/DTO/IProcessCustomImportCsvFileDTO";
import IOnProcessCustomImportCsvFileDTO from "@models/DTO/IOnProcessCustomImportCsvFileDTO";
import INotification from "@models/INotification";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import UiContext from "@contexts/UiContext";
import OwButton from "../OwButton/OwButton";

type Props = {
    type: CustomImportTypeEnum;
    existingOrderGuid?: string;
    onProcessed?: () => void;
};

export const CustomImportUploader = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const [loading, setLoading] = useState(true);
    const [customImports, setCustomImports] = useState<ICustomImport[]>([]);
    const [selectedCustomImport, setSelectedCustomImport] = useState<ICustomImport | null>(null);
    const [csvData, setCsvData] = useState<string | null>(null);
    const [uploadActive, setUploadActive] = useState(false);
    const [selectedFileName, setSelectedFileName] = useState<string | null>(null);

    const getCustomImports = () => {
        const payload: IGetCustomImportsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            type: props.type,
        };

        socket.emit(EndpointsEnum.CustomImportGetCustomImports, payload);
        setLoading(true);
    };

    const onGetCustomImports = (data: IOnGetCustomImportsDTO) => {
        setCustomImports(data.customImports);
        setLoading(false);
    };

    const selectCustomImport = (customImport: ICustomImport) => {
        setSelectedCustomImport(customImport);
    };

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];

        if (file) {
            const validExtensions = [".csv", ".txt"];
            const fileExtension = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();

            if (!validExtensions.includes(fileExtension)) {
                const notification: INotification = {
                    message: "invalid_file_type_csv_only",
                    level: NotificationLevelEnum.error,
                    position: NotificationPositionEnum.bc,
                };
                uiContext.notifyFn(null, notification);
                return;
            }

            const reader = new FileReader();

            reader.onload = (progressEvent: ProgressEvent<FileReader>) => {
                if (progressEvent.target?.result) {
                    setCsvData(progressEvent.target.result.toString());
                    setSelectedFileName(file.name);
                }
            };

            reader.onerror = () => {
                const notification: INotification = {
                    message: "error_reading_file",
                    level: NotificationLevelEnum.error,
                    position: NotificationPositionEnum.bc,
                };
                uiContext.notifyFn(null, notification);
            };

            reader.readAsText(file);
        } else {
            setCsvData(null);
            setSelectedFileName(null);
        }
    };

    const uploadCustomImport = () => {
        if (csvData && selectedCustomImport) {
            const payload: IProcessCustomImportCsvFileDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                customImportGuid: selectedCustomImport?.guid,
                csvData,
                options: {
                    existingOrderGuid: props.existingOrderGuid,
                },
            };

            socket.emit(EndpointsEnum.CustomImportProcessCustomImportCsvFile, payload);

            setUploadActive(true);
        }
    };

    const onProcessCustomImportCsvFile = (data: IOnProcessCustomImportCsvFileDTO) => {
        console.log("onProcessCustomImportCsvFile: ", data);

        const notification: INotification = {
            message: "successfully_processed_custom_import_csv",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        uiContext.notifyFn(null, notification);

        setUploadActive(false);
        setCsvData(null);
        setSelectedFileName(null);

        if (props.onProcessed) {
            props.onProcessed();
        }
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.CustomImportOnGetCustomImports, fn: onGetCustomImports },
        { endpoint: EndpointsEnum.CustomImportOnProcessCustomImportCsvFile, fn: onProcessCustomImportCsvFile },
    ];

    useEffect(() => {
        sockets.forEach((_socket) => {
            socket.on(_socket.endpoint, _socket.fn);
        });

        getCustomImports();

        return () => {
            sockets.forEach((_socket) => {
                socket.off(_socket.endpoint);
            });
        };
    }, []);

    return (
        <div className={"CustomImportUploader"}>
            {loading && <LoadingResults />}
            {!loading && (
                <div className="d-inline-flex flex-column">
                    <OwSelectOption
                        valueProp="guid"
                        titleProp="name"
                        emitObject={true}
                        fn={selectCustomImport}
                        name="customImport"
                        options={customImports}
                    />

                    <div className="mt-2">
                        <input type="file" accept=".csv" onChange={handleFileChange} className="form-control" />
                        {selectedFileName && <small className="text-muted mt-1 d-block">Selected: {selectedFileName}</small>}
                    </div>

                    <div className="mt-2">
                        <OwButton
                            title="upload"
                            fn={uploadCustomImport}
                            active={uploadActive}
                            disabled={!selectedCustomImport || !csvData}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
